import { useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { CLOUDFLARE_CERTIFICATE_UPLOAD_MUTATION } from '../../../../api/mutations/instances';
import { INSTANCE_QUERY } from '../../../../api/queries/instances';
import { useAppDispatch } from '../../../../helpers/reduxHooks';
import { setSuccessAlert } from '../../../../redux/alertSlice';

interface Props {
  domain: string,
  hasCertificate: boolean,
}

export default function CloudflareCertificateCard(props: Props) {
  const { domain, hasCertificate } = props;
  const { instanceId } = useParams();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(!hasCertificate);

  const [
    uploadMutation,
    { data: uploadData },
  ] = useMutation(CLOUDFLARE_CERTIFICATE_UPLOAD_MUTATION, {
    refetchQueries: [{ query: INSTANCE_QUERY, variables: { instance: instanceId } }],
  });

  const schema = yup.object({
    certificate: yup.string().required('Required'),
    privateKey: yup.string().required('Required'),
  });

  const {
    handleSubmit,
    handleChange,
    values: formikValues,
    errors: formikErrors,
    touched,
  } = useFormik({
    initialValues: {
      certificate: '',
      privateKey: '',
    },
    validationSchema: schema,
    onSubmit: (values) => {
      uploadMutation({
        variables: {
          instance: instanceId,
          domain,
          certificate: values.certificate,
          privateKey: values.privateKey,
        },
      });
    },
  });

  useEffect(() => {
    if (
      uploadData
      && uploadData.cloudflareCertificateUpload
      && uploadData.cloudflareCertificateUpload.response
    ) {
      dispatch(setSuccessAlert({
        messages: [`Origin CA certificate for ${domain} has been saved.`],
        instanceId: instanceId as string,
      }));
      setIsOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadData, dispatch]);

  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-bold text-slate-900">{domain}</span>
          {hasCertificate ? (
            <span className="ml-2 text-xs font-medium text-green-700 bg-green-100 rounded-full px-2 py-0.5">
              Certificate uploaded
            </span>
          ) : (
            <span className="ml-2 text-xs font-medium text-orange-700 bg-orange-100 rounded-full px-2 py-0.5">
              Certificate missing
            </span>
          )}
        </div>
        <button
          type="button"
          className="text-sm font-medium text-slate-600 hover:text-slate-900"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? 'Cancel' : (hasCertificate ? 'Replace' : 'Upload')}
        </button>
      </div>
      {isOpen && (
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-700">
              Origin CA Certificate (PEM)
            </label>
            <textarea
              rows={6}
              name="certificate"
              className="mt-1 block w-full font-mono text-xs border border-slate-300 rounded-md p-2"
              placeholder="-----BEGIN CERTIFICATE-----"
              value={formikValues.certificate}
              onChange={handleChange}
            />
            {touched.certificate && formikErrors.certificate && (
              <p className="mt-1 text-xs text-red-600">{formikErrors.certificate}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700">
              Private Key (PEM)
            </label>
            <textarea
              rows={6}
              name="privateKey"
              className="mt-1 block w-full font-mono text-xs border border-slate-300 rounded-md p-2"
              placeholder="-----BEGIN PRIVATE KEY-----"
              value={formikValues.privateKey}
              onChange={handleChange}
            />
            {touched.privateKey && formikErrors.privateKey && (
              <p className="mt-1 text-xs text-red-600">{formikErrors.privateKey}</p>
            )}
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700"
            >
              Save Certificate
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
